import { createTheme } from "@mui/material";

// Theme colors
export const colors = {
  // Core colors
  primary: "#5731FF", // Purple - primary UI elements
  secondary: "#8087ff", // Light purple for secondary elements
  accent: "#10a37f", // Accent color (similar to Manus green)

  // Background colors
  background: "#0f0f0f", // Darker background like Manus
  paper: "#1e1e1e", // Card/paper background
  cardHover: "#2a2a2a", // Hover state for cards

  // Code element colors
  codeBackground: "#2d2d2d",
  codeBorder: "#444",

  // Text colors
  text: {
    primary: "#ffffff",
    secondary: "#aaaaaa",
  },

  // Shadow colors
  shadowColor: "rgba(0, 0, 0, 0.2)",
};

// Code element styles - used in DevConsole.css and inline styles
export const codeStyles = {
  color: colors.secondary,
  fontWeight: 500,
  textShadow: `0 0 5px rgba(255, 113, 206, 0.6)`,
  backgroundColor: colors.codeBackground,
  padding: "4px 8px",
  borderRadius: "4px",
  fontFamily: "Roboto Mono, monospace",
  fontSize: "0.85em",
  border: `1px solid ${colors.codeBorder}`,
};

// Create the dark theme
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    background: {
      default: colors.background,
      paper: colors.paper,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8, // Increased border radius for all components
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body": {
          scrollbarWidth: "thin", // Firefox
          scrollbarColor: "#444 #1e1e1e", // Firefox
          // Calculate scrollbar width and set CSS variable
          "&": {
            "--scrollbar-width": "calc(100vw - 100%)",
          },
        },
        "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          background: "#1e1e1e",
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "#444",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "#555",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorPrimary: {
          color: colors.primary,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: `0 1px 2px rgba(0, 0, 0, 0.2)`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.paper,
          borderRadius: 12, // More rounded cards like Manus
          transition: "background-color 0.2s, box-shadow 0.2s",
          "&:hover": {
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: "hidden",
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 24px rgba(0, 0, 0, 0.3)",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 500,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
          },
        },
        containedPrimary: {
          background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          "&:hover": {
            background: `linear-gradient(90deg, ${colors.primary} 20%, ${colors.secondary} 120%)`,
          },
        },
      },
    },
    // Remove focus outlines from all Material-UI components
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&:focus": {
            outline: "none !important",
          },
          "&.Mui-focusVisible": {
            outline: "none !important",
          },
        },
      },
    },
    // Remove focus styles for all clickable components
    MuiButton: {
      styleOverrides: {
        root: {
          "&:focus": {
            outline: "none !important",
            boxShadow: "none !important",
          },
          "&.Mui-focusVisible": {
            outline: "none !important",
            boxShadow: "none !important",
          },
        },
      },
    },
    // Remove focus styles for inputs
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&.Mui-focused": {
            outline: "none !important",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: "1px",
            },
          },
        },
      },
    },
    // Remove focus styles for tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          "& .MuiTab-root": {
            "&:focus": {
              outline: "none !important",
            },
            "&.Mui-focusVisible": {
              outline: "none !important",
            },
          },
        },
      },
    },
    // Styling for list items
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "4px 0",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
          },
          "&.Mui-selected": {
            backgroundColor: `rgba(87, 49, 255, 0.1)`,
            "&:hover": {
              backgroundColor: `rgba(87, 49, 255, 0.15)`,
            },
          },
        },
      },
    },
  },
});

export default darkTheme;
